import React from "react";

import { deleteFromStorage, useLocalStorage } from "@rehooks/local-storage";
import PropTypes from "prop-types";

import SignUpContext from "./SignUpContext";

/**
 * Provide context for the sign up information.
 */
const SignUpContextProvider = ({ organizationId, widgetUUID = null, children }) => {
  const SIGN_UP_INFO_KEY = `signUpInfo-${organizationId}-${widgetUUID}`;

  const [signUpInfoLocalStorage, setSignUpInfoLocalStorage] = useLocalStorage(
    SIGN_UP_INFO_KEY,
    { info: { email: null, message: null } },
  );

  const info = signUpInfoLocalStorage?.info;

  const deleteSignUpInfoFromLocalStorage = React.useCallback(
    () => {
      if (SIGN_UP_INFO_KEY) {
        try {
          deleteFromStorage(SIGN_UP_INFO_KEY);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("Impossible to use localStorage to delete");
        }
      }
    },
    [SIGN_UP_INFO_KEY],
  );

  const setSignUpInfo = React.useCallback(
    (newInfo) => {
      setSignUpInfoLocalStorage({
        info: {
          ...signUpInfoLocalStorage.info,
          ...newInfo,
        },
      });
    },
    [signUpInfoLocalStorage, setSignUpInfoLocalStorage],
  );
  const value = {
    info,
    setSignUpInfo,
    deleteSignUpInfoFromLocalStorage,
  };

  return (
    <SignUpContext.Provider value={value}>
      {children}
    </SignUpContext.Provider>
  );
};

SignUpContextProvider.propTypes = {
  /**
   * The ID of the organization to scope the authentication.
   */
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /**
   * The UUID of the widget to scope the authentication.
   */
  widgetUUID: PropTypes.string,
  /**
   * The wrapped components that will have access to the context.
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.object]).isRequired,
};

export default SignUpContextProvider;
